import { useState, useEffect } from 'react';
import { DiscoveredAssets } from '../types';

interface UseScanProgressProps {
  onComplete: () => void;
}

export const useScanProgress = ({ onComplete }: UseScanProgressProps) => {
  const [progress, setProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState('Initializing scan...');
  const [discoveredAssets, setDiscoveredAssets] = useState<DiscoveredAssets>({
    domains: 0,
    subdomains: 0,
    ips: 0,
    vulnerabilities: 0
  });

  useEffect(() => {
    const tasks = [
      { task: 'Discovering root domains...', duration: 2000 },
      { task: 'Scanning subdomains...', duration: 3000 },
      { task: 'Identifying IP ranges...', duration: 2000 },
      { task: 'Detecting services...', duration: 2500 },
      { task: 'Running vulnerability scan...', duration: 3500 },
      { task: 'Finalizing results...', duration: 2000 }
    ];

    let currentIndex = 0;
    let startTime = Date.now();
    const totalDuration = tasks.reduce((acc, task) => acc + task.duration, 0);

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / totalDuration) * 100, 100);
      setProgress(newProgress);

      // Update current task
      let timeSum = 0;
      for (let i = 0; i < tasks.length; i++) {
        timeSum += tasks[i].duration;
        if (elapsed <= timeSum) {
          if (currentIndex !== i) {
            currentIndex = i;
            setCurrentTask(tasks[i].task);
            
            // Simulate discovering assets
            setDiscoveredAssets(prev => ({
              domains: Math.min(prev.domains + Math.floor(Math.random() * 3), 12),
              subdomains: Math.min(prev.subdomains + Math.floor(Math.random() * 10), 156),
              ips: Math.min(prev.ips + Math.floor(Math.random() * 5), 89),
              vulnerabilities: Math.min(prev.vulnerabilities + Math.floor(Math.random() * 3), 34)
            }));
          }
          break;
        }
      }

      if (newProgress < 100) {
        requestAnimationFrame(updateProgress);
      } else {
        setTimeout(onComplete, 1000);
      }
    };

    requestAnimationFrame(updateProgress);
  }, [onComplete]);

  return { progress, currentTask, discoveredAssets };
};